/**
 * The pure half of the long-task paths: the task rows and directive strings the
 * live sweep, the poller, and the on-load stranded-directive recovery build (ADR
 * 059). No persistence, no React, so each one is unit-testable without a renderer
 * and without a query client.
 */
import { buildTerminalDirective, markUntrusted } from "types/longTask";
import type { Task, TaskCreate, TerminalTask } from "types/Task";
import type { ResultLink, TerminalTaskStatus } from "types/TaskRegistry";
import {
  getTaskKind,
  shouldStopPolling,
  isKnownTerminalStatus,
} from "types/TaskRegistry";
import {
  pickLongTaskDisplayName,
  type SubmitEnvelope,
} from "utilities/longTaskResult";
import { oauthScopesForServer } from "utilities/mcpScopes";
import { getLongTaskGroup, type LongTaskGroup } from "./useLongTaskGroups";
import type { PendingInjection } from "./usePendingInjections";
import { FETCH_TRACKER_MIN_POLL_INTERVAL } from "./taskPollConstants";

/**
 * Builds the directive sent after the background fetch completes. Embeds links
 * directly so the agent presents them without another tool call. Instructs the
 * agent not to re-fetch: mode="required" fetch calls return a task handle, so
 * re-fetching would loop.
 *
 * Every embedded value comes from the fetch envelope, so each is marked as
 * untrusted data (ADR 059), the same treatment `interpolateDirective` gives a
 * server-supplied `reason`.
 */
export function buildFetchResultDirective(
  resultLinks: ResultLink[],
  archiveUrl: string | undefined
): string {
  const lines = [
    "The results fetch has completed. Present these final results to the user — do not call the fetch tool again, these results are final.",
  ];
  if (archiveUrl) {
    lines.push(`Complete analysis archive: ${markUntrusted(archiveUrl)}`);
  }
  if (resultLinks.length > 0) {
    lines.push("Reports:");
    for (const { label, permalink } of resultLinks) {
      lines.push(`- ${markUntrusted(label)}: ${markUntrusted(permalink)}`);
    }
  }
  return lines.join("\n");
}

interface BuildLongTaskFetchEntryOpts {
  fetchTaskId: string;
  /** The long-task job whose results this tracker fetches. */
  ownerJobId: string;
  toolName: string;
  threadId: string;
  serverUrl: string;
  groupName: string;
  pollInterval: number;
}

/** Builds the hidden tracker row registered when a *fetch* result carries a
 *  deferred task handle instead of the results inline. Every option is a row
 *  column, hence the spread. */
export function buildLongTaskFetchEntry(
  opts: BuildLongTaskFetchEntryOpts
): TaskCreate {
  return {
    ...opts,
    kind: "mcp-task",
    // The poller keys every row by `jobId`; `fetchTaskId` additionally tells the
    // backend to attach tracker fields to the row it already inserted for this
    // task (see postTask). Same value, two different columns.
    jobId: opts.fetchTaskId,
    pollInterval: Math.max(opts.pollInterval, FETCH_TRACKER_MIN_POLL_INTERVAL),
    status: "working",
    dismissed: true,
  };
}

/** Builds the `kind: "long-task"` row the live sweep registers for a submit result. */
export function buildLongTaskSubmitEntry(
  envelope: SubmitEnvelope,
  group: LongTaskGroup,
  toolName: string,
  threadId: string
): TaskCreate {
  return {
    kind: "long-task",
    toolName,
    displayName: pickLongTaskDisplayName(envelope),
    threadId,
    serverUrl: group.server,
    groupName: group.group,
    // Captured at submit so the poller resolves these from the durable row
    // rather than the session-only lifecycle map, which a persona switch
    // rebuilds (ADR 059, "statusTool resolution").
    statusTool: group.statusTool,
    oauthScopes: oauthScopesForServer(group.server),
    jobId: envelope.job_id,
    correlationId: envelope.correlation_id,
    status: "working",
  };
}

/**
 * Builds the `long-task-terminal` injection for a task row, so the terminal path
 * (`applyLongTaskTerminal`) and the stranded-terminal recovery produce the same
 * directive.
 *
 * The directive template is resolved from the lifecycle map here rather than
 * stored, so a server-side template change reaches in-flight jobs after a reload.
 *
 * `status` and `reason` are passed separately rather than read off the row: the
 * poller has just seen them and they may not be persisted yet.
 *
 * `status` and `reason` are passed to the injection as a record of how the job
 * ended. Nothing reads them back: the only consumer of a committed directive
 * is `hasCommittedTerminalDirective`.
 * The terminal status itself lives on the task row.
 */
export function buildTerminalInjectionForTask(
  task: Task,
  status: TerminalTaskStatus,
  reason: string | null
): PendingInjection {
  const groupName = task.groupName ?? "";
  return {
    id: crypto.randomUUID(),
    chatId: task.threadId,
    content: buildTerminalDirective(
      getLongTaskGroup(task.serverUrl ?? "", groupName),
      status,
      {
        groupName,
        jobId: task.jobId,
        correlationId: task.correlationId ?? undefined,
      },
      reason
    ),
    createdAt: new Date().toISOString(),
    longTaskJobId: task.jobId,
    kind: "long-task-terminal",
    status,
    statusReason: reason ?? undefined,
  };
}

/**
 * Returns long-task entries stranded by a crash between the terminal write and
 * directive enqueue: terminal with the one-shot flag set but no matching
 * committed directive in the message list.
 *
 * Selection uses `shouldStopPolling`, matching the poller, so an out-of-vocabulary
 * status (a stale pre-vocabulary-switch row mid-rollout) stays in scope here.
 * Narrowing it away instead would strand the row permanently: `shouldStopPolling`
 * counts an unknown status as terminal, so the poller excludes it too and no
 * other path would ever announce its terminal directive. Such a status is
 * normalized to `failed` for the announcement: the row is known terminal and
 * its one-shot claim is already spent, so reporting it imprecisely beats never
 * reporting it.
 */
export function findStrandedTerminalEntries(
  tasks: Task[],
  chatId: string,
  hasCommittedDirective: (jobId: string) => boolean
): TerminalTask[] {
  const isStranded = (task: Task): boolean =>
    getTaskKind(task) === "long-task" &&
    task.threadId === chatId &&
    !task.dismissed &&
    shouldStopPolling(task.status) &&
    !!task.terminalDirectiveInjectedAt &&
    !hasCommittedDirective(task.jobId);

  // `shouldStopPolling` above allows an unknown status but `TerminalTask` does not,
  // so those are normalized to `failed`.
  const toTerminalTask = (task: Task): TerminalTask =>
    isKnownTerminalStatus(task.status)
      ? { ...task, status: task.status }
      : { ...task, status: "failed" };

  return tasks.filter(isStranded).map(toTerminalTask);
}
